import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useSettings } from '../../context/SettingsContext';
import { getImageUrl } from '../../utils/helpers';
import { studyAbroadAPI } from '../../api/axios';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [dropdown, setDropdown] = useState(null);
    const [studyAbroadCountries, setStudyAbroadCountries] = useState([]);
    const location = useLocation();
    const navigate = useNavigate(); 
    const { settings, loading } = useSettings();

    // Scroll Logic
    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu and dropdowns when route changes
    useEffect(() => { setIsOpen(false); setDropdown(null); }, [location]);

    // Fetch dynamic study destinations
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const res = await studyAbroadAPI.getAll();
                setStudyAbroadCountries(res.data.data || []);
            } catch { setStudyAbroadCountries([]); }
        };
        fetchCountries();
    }, []);

    const handleNavClick = (e, path) => {
        if (path === '#') return;
        if (location.pathname === path) {
            e.preventDefault(); 
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setIsOpen(false); 
        }
    };

    const general = settings?.general || {};
    const headerSettings = settings?.header || {};
    const logoSrc = general.logo ? getImageUrl(general.logo) : null;
    const logoWhiteSrc = general.logoWhite ? getImageUrl(general.logoWhite) : null;
    
    const isTransparent = !scrolled && !isOpen && !loading;

    const fixStudyPath = (path) => {
        if (!path) return path;
        const match = path.match(/^\/study-in-(.+)$/);
        if (match && !path.startsWith('/study-in/')) return `/study-in/${match[1]}`;
        return path;
    };

    const buildNavLinks = () => {
        const baseLinks = headerSettings.menuItems || [];
        const studyChildren = studyAbroadCountries.map(country => ({
            name: `${country.flag || '🌍'} Study in ${country.countryName}`,
            path: `/study-in/${country.slug}`
        }));

        const defaultLinks = baseLinks.length > 0 ? baseLinks : [
            { name: 'Home', path: '/', isDropdown: false, children: [] },
            { name: 'About', path: '/about', isDropdown: false, children: [] },
            { name: 'Services', path: '/services', isDropdown: false, children: [] },
            { name: 'Study Destinations', path: '#', isDropdown: true, children: studyChildren },
            { name: 'Universities', path: '/universities', isDropdown: false, children: [] },
            { name: 'Courses', path: '/courses', isDropdown: false, children: [] },
            {
                name: 'More', path: '#', isDropdown: true,
                children: [
                    { name: '📰 Blog', path: '/blog' },
                    { name: '🏆 Scholarships', path: '/scholarships' },
                    { name: '📅 Events', path: '/events' },
                    { name: '📷 Gallery', path: '/gallery' },
                    { name: '❓ FAQ', path: '/faq' },
                    { name: '⭐ Testimonials', path: '/testimonials' },
                    { name: '🔍 Compare Universities', path: '/compare-universities' },
                    { name: '📋 Track Application', path: '/track-application' },
                ]
            },
            { name: 'Contact', path: '/contact', isDropdown: false, children: [] },
        ];

        return defaultLinks.map(link => {
            if (!link.isDropdown && link.path) return { ...link, path: fixStudyPath(link.path) };
            if (link.isDropdown && (link.name?.toLowerCase().includes('study') || link.name?.toLowerCase().includes('destination'))) {
                const fixedChildren = (link.children || []).map(child => ({ ...child, path: fixStudyPath(child.path) }));
                const existingPaths = fixedChildren.map(c => c.path?.toLowerCase());
                const dynamicChildren = studyAbroadCountries
                    .filter(country => !existingPaths.includes(`/study-in/${country.slug}`))
                    .map(country => ({ name: `${country.flag || '🌍'} Study in ${country.countryName}`, path: `/study-in/${country.slug}` }));
                return { ...link, children: [...fixedChildren, ...dynamicChildren] };
            }
            if (link.isDropdown && link.children) return { ...link, children: link.children.map(child => ({ ...child, path: fixStudyPath(child.path) })) };
            return link;
        });
    };

    const navLinks = buildNavLinks();

    const isActive = (path) => {
        if (!path || path === '#') return false;
        if (path === '/') return location.pathname === '/';
        return location.pathname === path || location.pathname.startsWith(path + '/') || location.pathname === fixStudyPath(path);
    };

    return (
        // SEO: Added aria-label to nav for accessibility & screen readers
        <nav 
            className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-500 ${
                isTransparent ? 'bg-transparent py-2' : 'bg-white shadow-[0_2px_20px_rgba(0,0,0,0.08)] py-0'
            }`}
            aria-label="Main navigation"
            role="navigation"
        >
            <div className="container mx-auto px-4">
                <div className="flex justify-between items-center h-20">

                    {/* SEO FIX: Changed <h1> to <span> — only ONE h1 should exist per page (in the page content)
                         This prevents duplicate h1 on every page which confuses search engines */}
                    <Link to="/" onClick={(e) => handleNavClick(e, '/')} className={`flex items-center space-x-3 transition-opacity duration-300`}>
                        {isTransparent ? (
                            logoWhiteSrc ? <img src={logoWhiteSrc} alt={`${general.siteName || 'Wise International Consultancy'} - Education Consultancy Nepal`} className="h-14 w-auto" width="56" height="56" /> :
                            logoSrc ? <img src={logoSrc} alt={`${general.siteName || 'Wise International Consultancy'} - Education Consultancy Nepal`} className="h-14 w-auto brightness-0 invert" width="56" height="56" /> :
                            <div className="w-14 h-14 bg-white/15 backdrop-blur-sm border border-white/20 rounded-xl flex items-center justify-center">
                                <span className="text-white font-bold text-lg">{general.siteName?.charAt(0) || 'W'}</span>
                            </div>
                        ) : (
                            logoSrc ? <img src={logoSrc} alt={`${general.siteName || 'Wise International Consultancy'} - Education Consultancy Nepal`} className="h-14 w-auto" width="56" height="56" /> :
                            <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20">
                                <span className="text-white font-bold text-lg">{general.siteName?.charAt(0) || 'W'}</span>
                            </div>
                        )}
                        <div>
                            {/* SEO CRITICAL FIX: h1 → span. The page's own h1 is the correct heading.
                                 Having h1 in the navbar means EVERY page has 2+ h1 tags = bad SEO */}
                            <span className={`text-lg font-bold leading-tight transition-colors duration-500 block ${isTransparent ? 'text-white drop-shadow-md' : 'text-gray-900'}`}>
                                {general.siteName || 'Wise International Consultancy'}
                            </span>
                            {general.tagline && (
                                <p className={`text-[11px] leading-tight transition-colors duration-500 ${isTransparent ? 'text-white/80 drop-shadow-md' : 'text-gray-500'}`}>
                                    {general.tagline}
                                </p>
                            )}
                        </div>
                    </Link>

                    {/* DESKTOP MENU */}
                    {/* SEO: Added role="menubar" for accessibility */}
                    <div className="hidden lg:flex items-center space-x-0.5" role="menubar">
                        {navLinks.map((link, index) => (
                            <div key={index} className="relative" role="none"
                                onMouseEnter={() => link.isDropdown ? setDropdown(index) : null}
                                onMouseLeave={() => link.isDropdown ? setDropdown(null) : null}>

                                {link.isDropdown ? (
                                    <>
                                        <button 
                                            className={`px-3.5 py-2 rounded-lg text-sm font-medium flex items-center transition-all duration-300 ${
                                                isTransparent
                                                    ? 'text-white hover:bg-white/20 drop-shadow-sm'
                                                    : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
                                                } ${dropdown === index ? (isTransparent ? 'bg-white/20 text-white' : 'bg-primary-50 text-primary-600') : ''}`}
                                            role="menuitem"
                                            aria-haspopup="true"
                                            aria-expanded={dropdown === index}
                                        >
                                            {link.name}
                                            <FaChevronDown className={`ml-1 w-2.5 h-2.5 transition-transform duration-200 ${dropdown === index ? 'rotate-180' : ''}`} aria-hidden="true" />
                                        </button>
                                        <AnimatePresence>
                                            {dropdown === index && (
                                                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.15 }}
                                                    className="absolute top-full left-0 w-64 pt-2" role="menu">
                                                    <div className="bg-white rounded-xl shadow-2xl shadow-gray-200/60 py-2 border border-gray-100 max-h-[400px] overflow-y-auto">
                                                        {link.children?.length > 0 ? (
                                                            link.children.map((child, ci) => (
                                                                <Link key={ci} to={child.path}
                                                                    onClick={(e) => handleNavClick(e, child.path)}
                                                                    role="menuitem"
                                                                    className={`block px-4 py-2.5 text-sm transition-colors ${
                                                                        isActive(child.path) ? 'text-primary-600 bg-primary-50 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                                                    }`}>
                                                                    {child.name}
                                                                </Link>
                                                            ))
                                                        ) : (
                                                            <p className="px-4 py-3 text-sm text-gray-400">No items</p>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </>
                                ) : (
                                    <Link to={link.path}
                                        onClick={(e) => handleNavClick(e, link.path)}
                                        role="menuitem"
                                        aria-current={isActive(link.path) ? 'page' : undefined}
                                        className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                                            isTransparent
                                                ? isActive(link.path) ? 'text-white bg-white/20 drop-shadow-sm' : 'text-white hover:bg-white/20 drop-shadow-sm'
                                                : isActive(link.path) ? 'text-primary-600 bg-primary-50' : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
                                        }`}>
                                        {link.name}
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* CTA BUTTONS */}
                    <div className="hidden lg:flex items-center space-x-2.5">
                        {headerSettings.ctaButton2?.visible && (
                            <Link to={headerSettings.ctaButton2.link || '/book-appointment'}
                                onClick={(e) => handleNavClick(e, headerSettings.ctaButton2.link || '/book-appointment')}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-all duration-500 ${
                                    isTransparent ? 'border-white/40 text-white hover:bg-white/10 backdrop-blur-sm' : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                                }`}>
                                {headerSettings.ctaButton2.text || 'Consultation'}
                            </Link>
                        )}
                        {headerSettings.ctaButton1?.visible && (
                            <Link to={headerSettings.ctaButton1.link || '/apply'}
                                onClick={(e) => handleNavClick(e, headerSettings.ctaButton1.link || '/apply')}
                                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-500 shadow-md ${
                                    isTransparent
                                        ? 'bg-primary-500 text-white hover:bg-primary-600 shadow-primary-500/30 border border-primary-400'
                                        : 'bg-primary-600 text-white hover:bg-primary-700 shadow-primary-600/20'
                                }`}>
                                {headerSettings.ctaButton1.text || 'Apply Now'}
                            </Link>
                        )}
                    </div>

                    {/* MOBILE BUTTON */}
                    <button 
                        className={`lg:hidden p-2 rounded-lg transition ${isTransparent ? 'text-white hover:bg-white/20' : 'text-gray-700 hover:bg-gray-100'}`}
                        onClick={() => setIsOpen(!isOpen)}
                        aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
                        aria-expanded={isOpen}
                    >
                        {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
                    </button>
                </div>
            </div>

            {/* MOBILE MENU */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                        className="lg:hidden bg-white border-t border-gray-100 overflow-hidden shadow-xl"
                        role="menu"
                        aria-label="Mobile navigation menu"
                    >
                        <div className="container mx-auto px-4 py-4 space-y-1 max-h-[80vh] overflow-y-auto">
                            {navLinks.map((link, index) => (
                                <div key={index} role="none">
                                    {link.isDropdown ? (
                                        <>
                                            <button onClick={() => setDropdown(dropdown === index ? null : index)}
                                                className="w-full flex justify-between items-center px-4 py-3 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
                                                role="menuitem"
                                                aria-haspopup="true"
                                                aria-expanded={dropdown === index}
                                            >
                                                {link.name}
                                                <FaChevronDown className={`w-3 h-3 transition-transform ${dropdown === index ? 'rotate-180 text-primary-600' : 'text-gray-400'}`} aria-hidden="true" />
                                            </button>
                                            <AnimatePresence>
                                                {dropdown === index && (
                                                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden" role="menu">
                                                        <div className="ml-4 pl-3 border-l-2 border-gray-200 py-1">
                                                            {link.children?.map((child, ci) => (
                                                                <Link key={ci} to={child.path}
                                                                    onClick={(e) => handleNavClick(e, child.path)}
                                                                    role="menuitem"
                                                                    className={`block px-4 py-2.5 text-sm rounded-lg ${
                                                                        isActive(child.path) ? 'text-primary-600 bg-primary-50 font-medium' : 'text-gray-600 hover:text-primary-600 hover:bg-gray-50'
                                                                    }`}>
                                                                    {child.name}
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </>
                                    ) : (
                                        <Link to={link.path} 
                                            onClick={(e) => handleNavClick(e, link.path)}
                                            role="menuitem"
                                            aria-current={isActive(link.path) ? 'page' : undefined}
                                            className={`block px-4 py-3 rounded-lg font-medium ${
                                            isActive(link.path) ? 'text-primary-600 bg-primary-50' : 'text-gray-700 hover:bg-gray-50'
                                        }`}>
                                            {link.name}
                                        </Link>
                                    )}
                                </div>
                            ))}
                            <div className="pt-4 mt-2 border-t border-gray-100 space-y-2">
                                {headerSettings.ctaButton1?.visible && (
                                    <Link to={headerSettings.ctaButton1.link || '/apply'} 
                                        onClick={(e) => handleNavClick(e, headerSettings.ctaButton1.link || '/apply')}
                                        className="block w-full text-center py-3 bg-primary-600 text-white font-semibold rounded-lg shadow-lg shadow-primary-600/20">
                                        {headerSettings.ctaButton1.text || 'Apply Now'}
                                    </Link>
                                )}
                                {headerSettings.ctaButton2?.visible && (
                                    <Link to={headerSettings.ctaButton2.link || '/book-appointment'} 
                                        onClick={(e) => handleNavClick(e, headerSettings.ctaButton2.link || '/book-appointment')}
                                        className="block w-full text-center py-3 bg-gray-50 text-gray-700 font-semibold rounded-lg border border-gray-200">
                                        {headerSettings.ctaButton2.text || 'Book Consultation'}
                                    </Link>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}

            </AnimatePresence>
        </nav>
    );
};

export default Navbar;